import React from 'react';

interface PasswordStrength {
  score: number; // 0-4
  label: 'Very Weak' | 'Weak' | 'Fair' | 'Good' | 'Strong';
  color: string;
}

interface PasswordStrengthIndicatorProps {
  password: string;
  className?: string;
}

const PasswordStrengthIndicator: React.FC<PasswordStrengthIndicatorProps> = ({
  password,
  className = ''
}) => {
  const calculatePasswordStrength = (password: string): PasswordStrength => {
    if (!password) {
      return { score: 0, label: 'Very Weak', color: '#EF4444' };
    }

    let score = 0;
    
    // Length check
    if (password.length >= 8) score++;
    if (password.length >= 12) score++;
    
    // Character variety checks
    if (/[a-z]/.test(password)) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;
    
    // Normalize score to 0-4 range
    const normalizedScore = Math.min(Math.floor(score / 1.5), 4);
    
    const strengthMap: Record<number, PasswordStrength> = {
      0: { score: 0, label: 'Very Weak', color: '#EF4444' },
      1: { score: 1, label: 'Weak', color: '#F97316' },
      2: { score: 2, label: 'Fair', color: '#EAB308' },
      3: { score: 3, label: 'Good', color: '#22C55E' },
      4: { score: 4, label: 'Strong', color: '#10B981' }
    };
    
    return strengthMap[normalizedScore];
  };

  const strength = calculatePasswordStrength(password);

  return (
    <div className={`password-strength-indicator ${className}`}>
      <div className="password-strength-bar">
        <div className="password-strength-segments">
          {[0, 1, 2, 3, 4].map((segment) => (
            <div
              key={segment}
              className={`password-strength-segment ${
                segment <= strength.score ? 'active' : ''
              }`}
              style={{
                backgroundColor: segment <= strength.score ? strength.color : '#E5E7EB'
              }}
            />
          ))}
        </div>
      </div>
      <div className="password-strength-label">
        <span 
          className="password-strength-text"
          style={{ color: strength.color }}
        >
          {strength.label}
        </span>
      </div>
    </div>
  );
};

export default PasswordStrengthIndicator;